// Auto-generated lightweight class for MBSE_Action
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { MBSE_ActionReadAPI, MBSE_ActionWriteAPI, MBSE_ActionReadAPISchema, MBSE_ActionWriteAPISchema } from '../types/mbse_action';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, HasMembers, MBSE_Allocates, MBSE_Attributes, MBSE_MainFunction, MBSE_Ports, MBSE_Satisfaction, MBSE_SubactionOf } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { SysMLNamespace } from '../../sysml/api/sysmlnamespace';
import { MBSE_Package } from './mbse_package';
import { MBSE_Port } from './mbse_port';
import { MBSE_Requirement } from './mbse_requirement';
import { MBSE_Part } from './mbse_part';
import { MBSE_Attribute } from './mbse_attribute';



export interface MBSE_Action extends Partial<MBSE_ActionReadAPI> {}
export class MBSE_Action extends NeoNodeBase<MBSE_ActionReadAPI, MBSE_ActionWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "MBSE_Action";
  static readonly readSchema = MBSE_ActionReadAPISchema;
  static readonly writeSchema = MBSE_ActionWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    member_namespace: { target: "SysMLNamespace", cardinality: "one", isParent: false, relClass: "HasMembers", direction: "in" },
    package: { target: "MBSE_Package", cardinality: "one", isParent: false, relClass: "MBSE_MainFunction", direction: "in" },
    parent: { target: "MBSE_Action", cardinality: "one", isParent: false, relClass: "MBSE_SubactionOf", direction: "in" },
    children: { target: "MBSE_Action", cardinality: "many", isParent: false, relClass: "MBSE_SubactionOf", direction: "out" },
    ports: { target: "MBSE_Port", cardinality: "many", isParent: false, relClass: "MBSE_Ports", direction: "out" },
    requirements: { target: "MBSE_Requirement", cardinality: "many", isParent: false, relClass: "MBSE_Satisfaction", direction: "in" },
    parts: { target: "MBSE_Part", cardinality: "many", isParent: false, relClass: "MBSE_Allocates", direction: "out" },
    attributes: { target: "MBSE_Attribute", cardinality: "many", isParent: false, relClass: "MBSE_Attributes", direction: "out" },
  };

  protected get modelName(): string {
    return 'MBSE_Action';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return MBSE_Action.label;
  }


  constructor(data: Partial<MBSE_ActionWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: MBSE_ActionReadAPI, loadContext?: Record<string, any>): MBSE_Action {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { security_scope, member_namespace, package: _package, parent, children, ports, requirements, parts, attributes, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = MBSE_ActionReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, security_scope: security_scope, member_namespace: member_namespace, package: _package, parent: parent, children: children, ports: ports, requirements: requirements, parts: parts, attributes: attributes };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return MBSE_Action.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'member_namespace', 'SysMLNamespace', ModelRegistry);
    loadOneRelationship(processed, 'package', 'MBSE_Package', ModelRegistry);
    loadOneRelationship(processed, 'parent', 'MBSE_Action', ModelRegistry);
    loadManyRelationship(processed, 'children', 'MBSE_Action', ModelRegistry);
    loadManyRelationship(processed, 'ports', 'MBSE_Port', ModelRegistry);
    loadManyRelationship(processed, 'requirements', 'MBSE_Requirement', ModelRegistry);
    loadManyRelationship(processed, 'parts', 'MBSE_Part', ModelRegistry);
    loadManyRelationship(processed, 'attributes', 'MBSE_Attribute', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new MBSE_Action(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'member_namespace' - Python ORM style (cached)
  get member_namespace(): RelationshipManager<this, SysMLNamespace> {
    return this.getOrCreateRelationshipManager('member_namespace', () => {
      return new RelationshipManager(this, 'member_namespace', 'one', HasMembers as typeof Relationship);
    });
  }

  // Relationship manager for 'package' - Python ORM style (cached)
  get package(): RelationshipManager<this, MBSE_Package> {
    return this.getOrCreateRelationshipManager('package', () => {
      return new RelationshipManager(this, 'package', 'one', MBSE_MainFunction as typeof Relationship);
    });
  }

  // Relationship manager for 'parent' - Python ORM style (cached)
  get parent(): RelationshipManager<this, MBSE_Action> {
    return this.getOrCreateRelationshipManager('parent', () => {
      return new RelationshipManager(this, 'parent', 'one', MBSE_SubactionOf as typeof Relationship);
    });
  }

  // Relationship manager for 'children' - Python ORM style (cached)
  get children(): RelationshipManager<this, MBSE_Action> {
    return this.getOrCreateRelationshipManager('children', () => {
      return new RelationshipManager(this, 'children', 'many', MBSE_SubactionOf as typeof Relationship);
    });
  }

  // Relationship manager for 'ports' - Python ORM style (cached)
  get ports(): RelationshipManager<this, MBSE_Port> {
    return this.getOrCreateRelationshipManager('ports', () => {
      return new RelationshipManager(this, 'ports', 'many', MBSE_Ports as typeof Relationship);
    });
  }

  // Relationship manager for 'requirements' - Python ORM style (cached)
  get requirements(): RelationshipManager<this, MBSE_Requirement> {
    return this.getOrCreateRelationshipManager('requirements', () => {
      return new RelationshipManager(this, 'requirements', 'many', MBSE_Satisfaction as typeof Relationship);
    });
  }

  // Relationship manager for 'parts' - Python ORM style (cached)
  get parts(): RelationshipManager<this, MBSE_Part> {
    return this.getOrCreateRelationshipManager('parts', () => {
      return new RelationshipManager(this, 'parts', 'many', MBSE_Allocates as typeof Relationship);
    });
  }

  // Relationship manager for 'attributes' - Python ORM style (cached)
  get attributes(): RelationshipManager<this, MBSE_Attribute> {
    return this.getOrCreateRelationshipManager('attributes', () => {
      return new RelationshipManager(this, 'attributes', 'many', MBSE_Attributes as typeof Relationship);
    });
  }

}
